import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { TLT_LOCATIONS } from '../data/tltLocations';
import tltLogo from '../../../assets/logos/TLT_Logo.png';
import { MAPBOX_TOKEN, hasScreenCredentials } from '../../../config';
import { fetchAllLocationsWeather } from '../../weather/services/weatherApi';
import { getWeatherIcon } from '../../weather/utils/weatherIcon';

// Token comes from frontend/.env (REACT_APP_MAPBOX_TOKEN) — never hard-code it.
mapboxgl.accessToken = MAPBOX_TOKEN;

const lightBaseMap = 'mapbox://styles/mapbox/light-v11';
const darkBaseMap = 'mapbox://styles/mapbox/dark-v11';

// --------------- Marker styles ---------------
const MARKER_CSS = `
  .tlt-pin {
    display: flex;
    flex-direction: column;
    align-items: center;
    pointer-events: none;
  }

  /* --- Weather floats above the logo, hidden until revealed --- */
  .tlt-weather {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 6px;
    opacity: 0;
    transform: translateY(8px) scale(0.8);
    transition: opacity 0.6s ease, transform 0.6s ease;
  }

  .tlt-weather.show {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  .tlt-weather-icon {
    font-size: 34px;
    filter: drop-shadow(0 3px 10px rgba(0,0,0,0.5));
    animation: iconBounce 2.5s ease-in-out infinite;
  }

  .tlt-temp {
    font-size: 15px;
    font-weight: 600;
    color: #ffffff;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    text-shadow: 0 1px 6px rgba(0,0,0,0.7);
    margin-top: 2px;
  }

  /* --- Logo: always visible --- */
  .tlt-logo {
    width: 52px;
    height: 52px;
    object-fit: contain;
    filter: drop-shadow(0 2px 10px rgba(0,0,0,0.6));
    animation: logoBob 3s ease-in-out infinite;
  }

  @keyframes logoBob {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-4px); }
  }

  @keyframes iconBounce {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-6px); }
  }
`;

// --------------- Helpers ---------------
const createPinElement = (location, logoUrl) => {
  const el = document.createElement('div');
  el.className = 'tlt-pin';
  el.dataset.slug = location.slug;
  el.innerHTML = `
    <div class="tlt-weather">
      <span class="tlt-weather-icon">${location.weather.icon}</span>
      <span class="tlt-temp">${location.weather.temperature}°</span>
    </div>
    <img class="tlt-logo" src="${logoUrl}" alt="TLT" />
  `;
  return el;
};

// --------------- Component ---------------
export const MapboxIntro = ({ targetLocation, onComplete }) => {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const timersRef = useRef([]);
  const spinRef = useRef(null);
  const completedRef = useRef(false);

  const hour = new Date().getHours();
  const isDayTime = hour >= 6 && hour < 18;
  const mapStyle = isDayTime ? lightBaseMap : darkBaseMap;

  const completeIntro = (location) => {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete(location);
  };

  useEffect(() => {
    if (mapRef.current) return;

    // Captured once, up front: timers/markers are only ever pushed onto
    // these same array instances (never reassigned), so the cleanup below
    // can safely close over these local bindings instead of re-reading
    // .current at cleanup time.
    const timers = timersRef.current;
    const markers = markersRef.current;

    const fallbackLocation = targetLocation || TLT_LOCATIONS[0];

    // A missing or malformed token (must start with "pk.") makes the map error
    // out instantly, which used to look like "the intro never plays". Skip the
    // map cleanly and make the cause obvious in the console.
    if (!MAPBOX_TOKEN || !MAPBOX_TOKEN.startsWith('pk.')) {
      console.error(
        'MapboxIntro: REACT_APP_MAPBOX_TOKEN is missing or malformed ' +
        `(got: ${JSON.stringify(MAPBOX_TOKEN)}). Check frontend/.env — plain ` +
        'KEY=VALUE, no quotes or semicolons — and restart the dev server.'
      );
      const t = setTimeout(() => completeIntro(fallbackLocation), 500);
      timers.push(t);
      return;
    }

    const failsafeTimer = setTimeout(() => completeIntro(fallbackLocation), 16000);
    timers.push(failsafeTimer);

    // --- 1. Create map as a globe ---
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapStyle,
      center: [-10, 25],       // start looking at Atlantic / Africa
      zoom: 1.5,
      pitch: 0,
      bearing: 0,
      projection: 'globe',
      antialias: true,
      interactive: false
    });
    mapRef.current = map;

    map.on('error', (event) => {
      console.error('Mapbox intro failed:', event?.error || event);
      const t = setTimeout(() => completeIntro(fallbackLocation), 1000);
      timers.push(t);
    });

    // Atmosphere / space effect + hide text labels
    map.on('style.load', () => {
      map.setFog({
        color: 'rgb(186, 210, 235)',
        'high-color': 'rgb(36, 92, 223)',
        'horizon-blend': 0.02,
        'space-color': 'rgb(11, 11, 25)',
        'star-intensity': 0.6
      });

    });

    map.on('load', () => {
      // --- Add pins immediately (with placeholder weather) so they're visible throughout ---
      TLT_LOCATIONS.forEach((loc) => {
        const el = createPinElement(loc, tltLogo);
        const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([loc.lng, loc.lat])
          .addTo(map);
        markers.push(marker);
      });

      // --- Swap in live station readings as soon as they arrive. The pins
      // already show TLT_LOCATIONS' static fallback values above, so a slow
      // or failed fetch just means those stay on screen a bit longer instead
      // of the pin going blank — same "keep the last good reading" approach
      // the rest of the display uses for weather. ---
      if (hasScreenCredentials()) {
        fetchAllLocationsWeather()
          .then(({ locations }) => {
            Object.entries(locations || {}).forEach(([slug, weather]) => {
              if (!weather) return; // no usable station reading — leave fallback showing
              const pinEl = mapContainer.current?.querySelector(`.tlt-pin[data-slug="${slug}"]`);
              if (!pinEl) return;
              const iconEl = pinEl.querySelector('.tlt-weather-icon');
              const tempEl = pinEl.querySelector('.tlt-temp');
              if (iconEl) iconEl.textContent = getWeatherIcon(weather, isDayTime);
              if (tempEl) tempEl.textContent = `${Math.round(weather.temperature)}°`;
            });
          })
          .catch((error) => {
            console.warn('MapboxIntro: could not load live weather for pins, showing fallback values.', error);
          });
      }

      // --- 2. Spin the globe for 3 seconds ---
      let lng = -10;
      const spin = () => {
        lng += 0.25;                       // ~15°/sec at 60 fps
        map.setCenter([lng, 25]);
        spinRef.current = requestAnimationFrame(spin);
      };
      spinRef.current = requestAnimationFrame(spin);

      // --- 3. After 3 s → fly to Pakistan ---
      const flyTimer = setTimeout(() => {
        cancelAnimationFrame(spinRef.current);

        map.flyTo({
          center: [73.1, 32.1],
          zoom: 5.6,
          pitch: 45,
          bearing: -10,
          duration: 4000,
          essential: true
        });

        // --- 4. After fly lands → reveal weather above each logo ---
        const revealTimer = setTimeout(() => {
          document.querySelectorAll('.tlt-weather').forEach((el) => {
            el.classList.add('show');
          });
        }, 4200);
        timers.push(revealTimer);

        // --- 5. After viewing for 3 s → zoom to this physical screen's branch ---
        const zoomTimer = setTimeout(() => zoomToLocation(map, fallbackLocation), 7500);
        timers.push(zoomTimer);
      }, 3000);
      timers.push(flyTimer);
    });

    return () => {
      if (spinRef.current) cancelAnimationFrame(spinRef.current);
      timers.forEach(clearTimeout);
      markers.forEach((m) => m.remove());
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const zoomToLocation = (map, location) => {
    map.flyTo({
      center: [location.lng, location.lat],
      zoom: 15,
      pitch: 60,
      bearing: 30,
      duration: 3000,
      essential: true
    });

    const t = setTimeout(() => completeIntro(location), 3500);
    timersRef.current.push(t);
  };

  return (
    <div style={{ width: '100vw', height: '100vh', position: 'relative', background: '#0b0b19' }}>
      <style>{MARKER_CSS}</style>
      <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};
